import { useState } from 'react'
import { Link } from 'react-router-dom'
import { formatRupees, scheduleLabel, pujaDateInfo } from '../../services/pujaFormat.js'

/** Branded stand-in when a puja image fails to load — dark maroon gradient
 * with an om mark, so the grid never shows a broken image. */
export function PoojaImageFallback({ className = '' }) {
  return (
    <div className={`w-full h-full bg-linear-to-br from-[#2B050C] via-[#1A0307] to-[#0A0103] flex items-center justify-center relative ${className}`}>
      <div className="absolute inset-0 bg-[radial-gradient(60%_60%_at_50%_40%,rgba(242,160,7,0.18),transparent_70%)]" />
      <div className="w-20 h-20 rounded-full border border-[#E6C878]/30 flex items-center justify-center">
        <i className="fa-solid fa-om text-3xl text-secondary"></i>
      </div>
    </div>
  )
}

/** First matching promo flag → badge copy. */
const flagBadge = (p) => {
  if (p.is_trending) return 'TRENDING'
  if (p.is_popular) return 'POPULAR'
  if (p.is_recommended) return 'RECOMMENDED'
  if (p.is_featured) return 'FEATURED'
  return null
}

/** Listing card for a PujaListItem row from GET /v1/pujas. */
export default function PoojaCard({ pooja: p }) {
  const [imgFailed, setImgFailed] = useState(false)
  const { weekday, label: dateLabel, daysLeft } = pujaDateInfo(p.puja_date)
  const badge = flagBadge(p)

  return (
    <Link
      to={`/pooja-energization/${p.slug}`}
      className="group relative bg-white border border-gray-100 rounded-3xl overflow-hidden transition-all duration-500 hover:border-secondary/40 hover:shadow-2xl hover:shadow-primary/5 flex flex-col h-full font-normal uppercase tracking-tight outline-none"
    >
      {/* IMAGE */}
      <div className="relative aspect-4/3 overflow-hidden bg-muted/10 border-b border-gray-100">
        {imgFailed || !p.image ? (
          <PoojaImageFallback />
        ) : (
          <img
            alt={p.title}
            loading="lazy"
            src={p.image}
            onError={() => setImgFailed(true)}
            className="object-cover w-full h-full transition-transform duration-1000 group-hover:scale-110"
          />
        )}
        <div className="absolute inset-0 bg-linear-to-t from-black/60 via-transparent to-transparent" />

        {/* Schedule / festival badge */}
        <div className="absolute top-4 left-4 z-10">
          <span className="bg-primary text-white px-3 py-1 rounded-lg text-[8px] font-bold tracking-widest border border-white/10 uppercase shadow-lg">
            <i className="fa-solid fa-calendar-day mr-1.5 text-secondary"></i>{scheduleLabel(p)}
          </span>
        </div>

        {/* Days-left, else promo flag */}
        {daysLeft != null && daysLeft >= 0 ? (
          <div className="absolute top-4 right-4 z-10">
            <span className="bg-secondary text-white px-3 py-1 rounded-lg text-[8px] font-bold tracking-widest border border-white/10 uppercase shadow-lg">
              {daysLeft === 0 ? 'TODAY' : daysLeft === 1 ? 'TOMORROW' : `IN ${daysLeft} DAYS`}
            </span>
          </div>
        ) : badge ? (
          <div className="absolute top-4 right-4 z-10">
            <span className="bg-secondary text-white px-3 py-1 rounded-lg text-[8px] font-bold tracking-widest border border-white/10 uppercase shadow-lg">
              <i className="fa-solid fa-fire mr-1"></i>{badge}
            </span>
          </div>
        ) : null}

        {/* Temple strip over the image */}
        {p.temple?.name && (
          <div className="absolute bottom-3 left-4 right-4 z-10 flex items-center gap-2 text-white/90">
            <i className="fa-solid fa-gopuram text-secondary text-xs shrink-0"></i>
            <span className="text-[9px] font-bold tracking-widest uppercase line-clamp-1">{p.temple.name}</span>
          </div>
        )}
      </div>

      {/* CONTENT */}
      <div className="p-6 flex flex-col flex-1 space-y-4">
        {/* Subtitle eyebrow */}
        {p.subtitle && (
          <p className="text-[9px] font-bold tracking-widest text-secondary uppercase line-clamp-1">
            <i className="fa-solid fa-star mr-1.5 text-[8px]"></i>{p.subtitle}
          </p>
        )}

        {/* Title */}
        <h3 className="text-base font-heading font-semibold text-primary leading-snug normal-case line-clamp-2">
          {p.title}
        </h3>

        {p.short_description && (
          <p className="text-[11px] text-primary/50 font-body leading-relaxed line-clamp-2 normal-case flex-1">
            {p.short_description}
          </p>
        )}

        {/* Date + category */}
        <div className="flex items-center justify-between text-[10px] text-primary/50 normal-case">
          <span className="flex items-center gap-2">
            <i className="fa-solid fa-calendar-check text-secondary text-[10px] w-3.5"></i>
            {dateLabel ? `${weekday}, ${dateLabel}` : scheduleLabel(p)}
          </span>
          {p.category?.title && (
            <span className="text-[8px] font-bold tracking-widest uppercase text-primary/40">{p.category.title}</span>
          )}
        </div>

        {/* Price + CTA */}
        <div className="pt-4 border-t border-dashed border-gray-100 flex items-center justify-between mt-auto gap-3">
          <div className="flex flex-col">
            <span className="text-[8px] text-primary/30 uppercase tracking-[0.2em] mb-1">STARTS AT</span>
            <span className="text-xl font-bold text-primary">{formatRupees(p.starting_price)}</span>
          </div>
          <span className="inline-flex items-center gap-2 h-10 px-5 rounded-xl bg-secondary text-white text-[9px] font-bold tracking-widest uppercase shadow-lg shadow-secondary/20 transition-all group-hover:bg-primary">
            Book Puja <i className="fa-solid fa-arrow-right text-[9px] transition-transform group-hover:translate-x-0.5"></i>
          </span>
        </div>
      </div>
    </Link>
  )
}
